import * as React from "react";
import { Box, Stack, Typography, IconButton, Tooltip, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    Handle,
    Position,
    MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import FloatingEdge from "../FloatingEdge";
import FloatingConnectionLine from "../FloatingConnectionLine";

// --------------------
// Theme tokens
// --------------------
const ACCENT = "#774CBC";
const ACCENT_SOFT = "rgba(119,76,188,0.10)";
const BORDER = "rgba(17, 24, 39, 0.10)";
const TEXT = "rgba(17, 24, 39, 0.92)";
const MUTED = "rgba(17, 24, 39, 0.60)";



function safeParseFlow(flowJsonString) {
    if (!flowJsonString) return null;
    if (typeof flowJsonString === "object") return flowJsonString;
    try {
        return JSON.parse(flowJsonString);
    } catch {
        return null;
    }
}

// --------------------
// Node Components
// --------------------
function StartEndNode({ data, variant = "start" }) {
    const isStart = variant === "start";

    return (
        <Box
            sx={{
                px: 2.2,
                py: 1.2,
                borderRadius: 999,
                border: `1px solid ${BORDER}`,
                bgcolor: "#fff",
                boxShadow: "0 10px 24px rgba(17,24,39,0.10)",
                minWidth: 140,
                textAlign: "center",
                position: "relative",
                "&:before": {
                    content: '""',
                    position: "absolute",
                    inset: -2,
                    borderRadius: 999,
                    background: isStart
                        ? `linear-gradient(135deg, ${ACCENT} 0%, rgba(119,76,188,0.35) 60%, rgba(119,76,188,0.12) 100%)`
                        : `linear-gradient(135deg, rgba(119,76,188,0.12) 0%, rgba(119,76,188,0.35) 40%, ${ACCENT} 100%)`,
                    zIndex: -1,
                    filter: "blur(10px)",
                    opacity: 0.55,
                },
            }}
        >
            <Typography sx={{ fontWeight: 900, fontSize: 13, color: TEXT }}>
                {data?.label ?? (isStart ? "Start" : "End")}
            </Typography>
            <Typography sx={{ fontSize: 11, color: MUTED }}>
                {isStart ? "ENTRY" : "EXIT"}
            </Typography>

            {/* Uwaga: handle ukryte, ale muszą istnieć */}
            <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
            <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
        </Box>
    );
}

function toDataImageSrc(img) {
    if (!img || typeof img !== "string") return null;
    if (img.startsWith("data:image")) return img;

    // heurystyka: jak wygląda jak base64 to jpeg, ale możesz zmienić na png jeśli u Ciebie często png
    return `data:image/jpeg;base64,${img}`;
}

function ImageThumb({ img }) {
    const src = toDataImageSrc(img);
    if (!src) return null;

    return (
        <Box
            sx={{
                position: "relative",
                mt: 1,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid rgba(17,24,39,0.10)",
                bgcolor: "rgba(17,24,39,0.02)",
                height: 'auto',
                "&:hover .preview": { opacity: 1, transform: "translateY(0px)" },
            }}
        >
            <Box
                component="img"
                src={src}
                alt=""
                onError={(e) => (e.currentTarget.style.display = "none")}
                sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    filter: "saturate(1.05) contrast(1.02)",
                }}
            />

            {/* hover preview */}
            <Box
                className="preview"
                sx={{
                    position: "absolute",
                    left: 10,
                    bottom: 10,
                    width: 260,
                    height: 180,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid rgba(17,24,39,0.12)",
                    bgcolor: "#fff",
                    boxShadow: "0 18px 50px rgba(17,24,39,0.18)",
                    opacity: 0,
                    transform: "translateY(6px)",
                    transition: "all 120ms ease",
                    pointerEvents: "none",
                }}
            >
                <Box
                    component="img"
                    src={src}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "contain", bgcolor: "#fff" }}
                />
            </Box>
        </Box>
    );
}

function CustomActionNode({ data }) {
    const label = data?.label ?? "Step";
    const fn = data?.function_name ?? "";
    const lib = data?.library ?? "";
    const category = Array.isArray(data?.category) ? data.category.join(", ") : "";

    return (
        <Box
            sx={{
                px: 2,
                py: 1.5,
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: "#fff",
                boxShadow: "0 12px 30px rgba(17,24,39,0.10)",
                minWidth: 280,
                maxWidth: 380,
                position: "relative",
                overflow: "hidden",
                "&:after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: `linear-gradient(180deg, ${ACCENT} 0%, rgba(119,76,188,0.20) 100%)`,
                },
            }}
        >
            <Stack spacing={0.8}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography
                        sx={{
                            fontWeight: 950,
                            fontSize: 13,
                            color: TEXT,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 240,
                        }}
                        title={label}
                    >
                        {label}
                    </Typography>

                    {category ? (
                        <Chip
                            size="small"
                            label={category}
                            sx={{
                                fontWeight: 800,
                                bgcolor: ACCENT_SOFT,
                                border: `1px solid rgba(119,76,188,0.25)`,
                                color: ACCENT,

                                maxWidth: 150,

                                // najważniejsze:
                                height: "auto",
                                alignItems: "flex-start",
                                "& .MuiChip-label": {
                                    whiteSpace: "normal",
                                    overflow: "hidden",
                                    textOverflow: "clip",
                                    display: "block",
                                    lineHeight: 1.2,
                                    paddingTop: "4px",
                                    paddingBottom: "4px",
                                },
                            }}
                        />

                    ) : null}
                </Stack>

                {/*{!!fn && <Typography sx={{ fontSize: 12, color: MUTED }}>{fn}</Typography>}*/}
                {/*{!!lib && (*/}
                {/*    <Typography sx={{ fontSize: 11, color: "rgba(17,24,39,0.52)" }}>{lib}</Typography>*/}
                {/*)}*/}

                {/* IMG thumbnail + hover preview */}
                <ImageThumb img={data?.img} />

                {/* param chips */}
                {Array.isArray(data?.parameters) && data.parameters.length > 0 && (
                    <Box sx={{ mt: 0.5, display: "flex", gap: 0.6, flexWrap: "wrap" }}>
                        {data.parameters.slice(0, 3).map((p, idx) => (
                            <Chip
                                key={`${p.key}-${idx}`}
                                size="small"
                                label={`${p.key}: ${String(p.currentValue ?? p.defaultValue ?? "")}`.slice(0, 28)}
                                sx={{
                                    height: 22,
                                    bgcolor: "rgba(17,24,39,0.04)",
                                    border: `1px solid rgba(17,24,39,0.08)`,
                                    fontFamily: "monospace",
                                    fontSize: 11,
                                }}
                            />
                        ))}
                        {data.parameters.length > 3 && (
                            <Chip
                                size="small"
                                label={`+${data.parameters.length - 3}`}
                                sx={{
                                    height: 22,
                                    bgcolor: ACCENT_SOFT,
                                    border: `1px solid rgba(119,76,188,0.25)`,
                                    color: ACCENT,
                                    fontWeight: 800,
                                }}
                            />
                        )}
                    </Box>
                )}
            </Stack>

            <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
            <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
        </Box>
    );
}

// IF / Gateway node: romb + dwa wyjścia (cond-0 i else)
function IfNode({ data }) {
    const title = data?.label ?? "If";
    const conditions = Array.isArray(data?.conditions) ? data.conditions : [];

    // ile wyjść warunkowych rysujemy (max 3 żeby nie robić choinki)
    const shown = conditions.slice(0, 3);
    const extra = Math.max(0, conditions.length - shown.length);

    return (
        <Box
            sx={{
                px: 2,
                py: 1.5,
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: "#fff",
                boxShadow: "0 12px 30px rgba(17,24,39,0.10)",
                minWidth: 280,
                maxWidth: 380,
                position: "relative",
                overflow: "hidden",
                "&:after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: `linear-gradient(180deg, ${ACCENT} 0%, rgba(119,76,188,0.20) 100%)`,
                },
            }}
        >
            {/* Handles */}
            <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
            {shown.map((_, i) => (
                <Handle
                    key={`cond-${i}`}
                    type="source"
                    id={`cond-${i}`}
                    position={Position.Right}
                    style={{ opacity: 0 }}
                />
            ))}
            <Handle type="source" id="else" position={Position.Right} style={{ opacity: 0 }} />

            <Stack spacing={0.8}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 950, fontSize: 13, color: TEXT }}>
                        {title}
                    </Typography>

                    <Chip
                        size="small"
                        label="Branch"
                        sx={{
                            fontWeight: 800,
                            height: 22,
                            bgcolor: ACCENT_SOFT,
                            border: `1px solid rgba(119,76,188,0.25)`,
                            color: ACCENT,
                        }}
                    />
                </Stack>

                {/* Mały “gateway” symbol w środku, ale dalej karta */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 2,
                            border: "1px solid rgba(119,76,188,0.30)",
                            bgcolor: "rgba(119,76,188,0.06)",
                            display: "grid",
                            placeItems: "center",
                        }}
                    >
                        <Box
                            sx={{
                                width: 14,
                                height: 14,
                                transform: "rotate(45deg)",
                                borderRadius: 1,
                                border: "1px solid rgba(119,76,188,0.55)",
                                bgcolor: "rgba(119,76,188,0.10)",
                            }}
                        />
                    </Box>

                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {shown[0]?.label ?? data?.parameters?.[0]?.label ?? ""}
                    </Typography>
                </Box>

                {/* “Ports”/labels – MUSZĄ zgadzać się z JSON */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mt: 0.5 }}>
                    {shown.map((c, i) => (
                        <Chip
                            key={`c-${i}`}
                            size="small"
                            label={String(c?.label ?? `cond-${i}`)}
                            sx={{
                                height: 22,
                                bgcolor: "rgba(17,24,39,0.04)",
                                border: "1px solid rgba(17,24,39,0.10)",
                                fontSize: 11,
                            }}
                        />
                    ))}

                    <Chip
                        size="small"
                        label="Else"
                        sx={{
                            height: 22,
                            bgcolor: ACCENT_SOFT,
                            border: "1px solid rgba(119,76,188,0.25)",
                            color: ACCENT,
                            fontWeight: 800,
                            fontSize: 11,
                        }}
                    />

                    {extra > 0 && (
                        <Chip
                            size="small"
                            label={`+${extra} more`}
                            sx={{
                                height: 22,
                                bgcolor: "rgba(17,24,39,0.04)",
                                border: "1px solid rgba(17,24,39,0.10)",
                                fontSize: 11,
                            }}
                        />
                    )}
                </Box>
            </Stack>
        </Box>
    );
}

const nodeTypes = {
    startNode: (props) => <StartEndNode {...props} variant="start" />,
    endNode: (props) => <StartEndNode {...props} variant="end" />,
    custom: CustomActionNode,
    ifNode: IfNode,
};


// --------------------
// Floating edge types
// --------------------
const edgeTypes = { floating: FloatingEdge };

function getNodeSize(n) {
    // prefer: width/height z JSON, potem measured, potem style, potem fallback per typ
    const w =
        Number(n?.width) ||
        Number(n?.measured?.width) ||
        Number(n?.style?.width) ||
        (n.type === "ifNode" ? 280 : n.type === "custom" ? 300 : 160);

    const h =
        Number(n?.height) ||
        Number(n?.measured?.height) ||
        Number(n?.style?.height) ||
        (n.type === "ifNode" ? 110 : n.type === "custom" ? 140 : 60);

    return { w, h };
}

function rectOf(node, pos, pad) {
    const { w, h } = getNodeSize(node);
    return {
        left: pos.x - pad,
        top: pos.y - pad,
        right: pos.x + w + pad,
        bottom: pos.y + h + pad,
        w,
        h,
    };
}

function intersects(a, b) {
    return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);
}

/**
 * Prostą korekta kolizji: iteracyjnie przesuwa “kolizyjny” node w prawo/dół,
 * zachowując w miarę oryginalny układ. Nie jest to pełny layout engine,
 * ale daje stabilne odstępy przy zachowaniu Twoich pozycji.
 */
function applyAntiOverlap(rawNodes, padding = 26, maxIters = 6) {
    const nodes = rawNodes.map((n) => ({
        ...n,
        position: { ...n.position },
    }));

    for (let iter = 0; iter < maxIters; iter++) {
        let moved = false;

        for (let i = 0; i < nodes.length; i++) {
            for (let j = 0; j < i; j++) {
                const ni = nodes[i];
                const nj = nodes[j];

                const ri = rectOf(ni, ni.position, padding);
                const rj = rectOf(nj, nj.position, padding);

                if (!intersects(ri, rj)) continue;

                // minimalny wektor wypchnięcia: wybierz mniejszy z (w prawo) vs (w dół)
                const pushRight = rj.right - ri.left + padding;
                const pushDown = rj.bottom - ri.top + padding;

                if (pushRight < pushDown) {
                    ni.position.x += pushRight;
                } else {
                    ni.position.y += pushDown;
                }

                moved = true;
            }
        }

        if (!moved) break;
    }

    return nodes;
}

function normalizeNodes(flow) {
    const raw = flow?.nodes ?? [];

    const normalized = raw.map((n) => {
        const position =
            n.position ??
            (n.positionAbsolute ? { x: n.positionAbsolute.x, y: n.positionAbsolute.y } : { x: 0, y: 0 });

        const { w, h } = getNodeSize(n);

        return {
            ...n,
            type: n.type || "custom",
            data: n.data || {},
            position,
            draggable: false,
            selectable: false,
            connectable: false,
            deletable: false,
            focusable: false,
            // ważne: width/height “twardo” żeby RF wiedział jak liczyć fitView i żeby collision miało sens
            width: Number(n?.width) || Number(n?.measured?.width) || w,
            height: Number(n?.height) || Number(n?.measured?.height) || h,
            style: {
                ...(n.style || {}),
                width: Number(n?.width) || Number(n?.measured?.width) || w,
                height: Number(n?.height) || Number(n?.measured?.height) || h,
            },
        };
    });

    // Anti-overlap pass (możesz zmienić padding np. 32)
    return applyAntiOverlap(normalized, 50, 2);
}


function normalizeEdges(flow) {
    const raw = flow?.edges ?? [];
    const nodesById = new Map((flow?.nodes ?? []).map((n) => [String(n.id), n]));

    return raw.map((e, i) => {
        const src = nodesById.get(String(e.source));
        const conds = src?.data?.conditions;

        let inferred = "";
        if (e.sourceHandle?.startsWith("cond-") && Array.isArray(conds)) {
            const idx = Number(e.sourceHandle.replace("cond-", ""));
            inferred = String(conds?.[idx]?.label ?? "");
        } else if (e.sourceHandle === "else") {
            inferred = "Else";
        }

        return {
            ...e,
            id: e.id ?? `e-${i}-${e.source}-${e.target}`,
            type: "floating",
            animated: false,
            // jeżeli w JSON edge ma label -> zostaw, inaczej inferuj z conditions
            label: e.label ?? inferred,
            style: {
                stroke: "rgba(119,76,188,0.90)",
                strokeWidth: 2.2,
                ...(e.style || {}),
            },
            markerEnd: e.markerEnd ?? {
                type: MarkerType.ArrowClosed,
                color: "rgba(119,76,188,0.90)",
            },
            selectable: false,
            focusable: false,
        };
    });
}

export default function ReadOnlyWorkflowViewer({
    flowJsonString,
    title = "Process Flow",
    subtitle,
    onClose,
}) {
    const flow = React.useMemo(() => safeParseFlow(flowJsonString), [flowJsonString]);

    const proOptions = { account: "paid-pro", hideAttribution: true };

    const nodes = React.useMemo(() => normalizeNodes(flow), [flow]);
    const edges = React.useMemo(() => normalizeEdges(flow), [flow]);

    if (!flow) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="error">Invalid flow payload (JSON parse failed).</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100%",
                minHeight: 560,
                borderRadius: 3,
                overflow: "hidden",
                border: `1px solid ${BORDER}`,
                bgcolor: "#fff",
                position: "relative",
                zIndex: 500,
            }}
        >
            {/* Top bar */}
            <Box
                sx={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    right: 10,
                    zIndex: 5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.75,
                    py: 1.2,
                    borderRadius: 2.5,
                    border: `1px solid ${BORDER}`,
                    bgcolor: "#fff",
                    boxShadow: "0 10px 24px rgba(17,24,39,0.08)",
                    pointerEvents: "auto",
                }}
            >
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        bgcolor: ACCENT,
                        boxShadow: `0 0 0 6px ${ACCENT_SOFT}`,
                    }}
                />

                <Stack spacing={0}>
                    <Typography sx={{ fontWeight: 950, color: TEXT, lineHeight: 1.1 }}>
                        {title}
                    </Typography>

                    {(subtitle ?? flow?.description) ? (
                        <Typography sx={{ fontSize: 12, color: MUTED }}>
                            {subtitle ?? flow.description}
                        </Typography>
                    ) : null}
                </Stack>

                <Box sx={{ flex: 1 }} />

                { (
                    <Tooltip title="Close">
                        <IconButton
                            size="small"
                            onClick={onClose}
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${BORDER}`,
                                color: TEXT,
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Flow */}
            <Box sx={{ position: "absolute", inset: 0 }}>
                <ReactFlow
                    proOptions={proOptions}
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    connectionLineComponent={FloatingConnectionLine}
                    // READ ONLY
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    panOnDrag
                    zoomOnScroll
                    zoomOnPinch
                    minZoom={0.2}
                    maxZoom={3}
                    defaultEdgeOptions={{
                        type: "floating",
                        style: { stroke: "rgba(119,76,188,0.90)", strokeWidth: 2.2 },
                        markerEnd: { type: MarkerType.ArrowClosed, color: "rgba(119,76,188,0.90)" },
                        labelBgPadding: [6, 3],
                        labelBgBorderRadius: 4,
                        labelBgStyle: { fill: "#fff", fillOpacity: 0.85 },
                    }}
                    // KLUCZ: widok po init — albo viewport z JSON, albo fitView
                    onInit={(instance) => {
                        const vp = flow?.viewport;
                        if (vp && Number.isFinite(vp.x) && Number.isFinite(vp.y) && Number.isFinite(vp.zoom)) {
                            instance.setViewport({ x: vp.x, y: vp.y, zoom: vp.zoom }, { duration: 0 });
                        } else {
                            // fitView czasem działa dopiero “po ticku”
                            requestAnimationFrame(() => instance.fitView({ padding: 0.25, duration: 0 }));
                        }
                    }}
                >
                    <Background variant="dots" gap={18} size={1} color="rgba(119,76,188,0.10)" />


                    <MiniMap
                        position="bottom-left"
                        style={{
                            background: "#fff",
                            border: `1px solid ${BORDER}`,
                            borderRadius: 12,
                            overflow: "hidden",
                            boxShadow: "0 10px 24px rgba(17,24,39,0.08)",
                        }}
                        nodeStrokeWidth={2}
                        zoomable
                        pannable
                    />
                </ReactFlow>
            </Box>
        </Box>
    );
}
